"use client";

import { IconCopy, IconEye, IconEyeOff, IconPencil, IconPlus, IconRefresh, IconTrash } from "@tabler/icons-react";
import { gql } from "graphql-request";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { CreateApiKeyDialog } from "@/components/api-keys/create-api-key-dialog";
import { CapacityNoticeBanner } from "@/components/capacity-notice-banner";
import { IntegrationCodeSheet } from "@/components/integration-code/integration-code-sheet";
import { OrgProjectChip, OrgProjectColorDot } from "@/components/org/org-project-chip";
import { type RowActionItem, RowActionsMenu } from "@/components/org/row-actions-menu";
import { PageHeader } from "@/components/page-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/snackbar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth, useSession } from "@/lib/auth";
import { capacityBlockedDetailForTooltip } from "@/lib/capacity-status-detail";
import { useGraphqlMutation, useGraphqlQuery } from "@/lib/graphql/useGraphql";
import {
  ASK_ADMIN_TO_CREATE_PROJECT_HINT,
  orgRoleCanCreateProjects,
  orgRoleCanMutateKeys,
} from "@/lib/org/org-constants";
import { toastUserError } from "@/lib/toast-user-error";

const deployKeysDocument = gql`
  query DeployKeysPage {
    me {
      org {
        projects {
          id
          name
          color
        }
      }
    }
    apiKeys(status: "active") {
      id
      name
      key_prefix
      purpose
      is_editable
      status
      created_at
      last_used_at
      project_id
      revealed_at
    }
  }
`;

const revealApiKeyDocument = gql`
  mutation DeployKeysRevealApiKey($id: ID!) {
    revealApiKey(id: $id) {
      raw_key
      api_key {
        id
        revealed_at
      }
    }
  }
`;

const rotateApiKeyDocument = gql`
  mutation DeployKeysRotateApiKey($id: ID!) {
    rotateApiKey(id: $id) {
      old_api_key {
        id
        status
      }
      api_key {
        id
        name
      }
      raw_key
    }
  }
`;

const revokeApiKeyDocument = gql`
  mutation DeployKeysRevokeApiKey($id: ID!) {
    revokeApiKey(id: $id) {
      id
      status
      revoked_at
    }
  }
`;

const renameApiKeyDocument = gql`
  mutation DeployKeysRenameApiKey($id: ID!, $name: String!) {
    renameApiKey(id: $id, name: $name) {
      id
      name
    }
  }
`;

const provisionProjectDocument = gql`
  mutation DeployKeysProvisionProject {
    provisionProject {
      id
      name
    }
  }
`;

type DeployKeysQuery = {
  me: {
    org: {
      projects: Array<{ id: string; name: string; color?: string | null }>;
    } | null;
  };
  apiKeys: Array<{
    id: string;
    name: string;
    key_prefix: string;
    purpose: string;
    is_editable: boolean;
    status: string;
    created_at: string;
    last_used_at: string | null;
    project_id: string | null;
    revealed_at: string | null;
  }>;
};

const CHAT_DEFAULT_PURPOSE = "chat_default";
const MASK_PLACEHOLDER_LENGTH = 24;

const maskFromPrefix = (prefix: string) => `${prefix}${"•".repeat(MASK_PLACEHOLDER_LENGTH)}`;

const isBillingRequiredError = (error: unknown): boolean => {
  const gqlError = error as { response?: { errors?: Array<{ extensions?: { code?: string } }> } };
  return gqlError?.response?.errors?.some((e) => e?.extensions?.code === "BILLING_REQUIRED") ?? false;
};

const graphqlValidationMessage = (error: unknown): string | null => {
  const gqlError = error as {
    response?: {
      errors?: Array<{
        message?: string;
        extensions?: { validation?: Record<string, string[] | string> };
      }>;
    };
  };
  const first = gqlError.response?.errors?.[0];
  const validation = first?.extensions?.validation;
  if (validation) {
    const messages = Object.values(validation).flat();
    const firstMessage = messages.find((message) => typeof message === "string" && message.trim() !== "");
    if (firstMessage) {
      return firstMessage;
    }
  }
  return first?.message?.trim() || null;
};

/**
 * Write text to the clipboard via the legacy execCommand approach.
 * Works without an active user-gesture in all major browsers by temporarily
 * focusing a hidden textarea. Returns true on success.
 */
const writeViaExecCommand = (text: string): boolean => {
  try {
    const el = document.createElement("textarea");
    el.value = text;
    el.style.cssText = "position:fixed;top:0;left:-9999px;opacity:0;pointer-events:none";
    document.body.appendChild(el);
    el.focus();
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
};

const APIKeysPage = () => {
  const { data: session, isPending } = useSession();
  const { capacityStatus, orgRole } = useAuth();
  const router = useRouter();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [integrationCodeOpen, setIntegrationCodeOpen] = useState(false);
  const [createdApiKey, setCreatedApiKey] = useState<{ name: string; rawKey: string } | null>(null);
  const [confirmKind, setConfirmKind] = useState<"rotate" | "revoke" | null>(null);
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameDraft, setRenameDraft] = useState("");
  const [renameError, setRenameError] = useState<string | null>(null);
  const [projectFilter, setProjectFilter] = useState<string>("all");

  // Per-key reveal state. Both maps are keyed by API key ID.
  // revealedKeys: key ID → raw key value (set on first successful reveal).
  // visibleKeys:  set of key IDs currently showing the raw value (toggled by the eye button).
  // revealingKeyId: the key currently being fetched from the server (shows loading state).
  const [revealedKeys, setRevealedKeys] = useState<Map<string, string>>(new Map());
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
  const [revealingKeyId, setRevealingKeyId] = useState<string | null>(null);

  const canMutateKeys = orgRoleCanMutateKeys(orgRole);
  const canCreateProjects = orgRoleCanCreateProjects(orgRole);
  const canMutate = canMutateKeys && capacityStatus?.state === "granted";

  const capacityTooltip = useMemo(() => capacityBlockedDetailForTooltip(capacityStatus), [capacityStatus]);

  const keysQuery = useGraphqlQuery<DeployKeysQuery>({
    queryKey: ["apiKeys", "deploy-keys"],
    document: deployKeysDocument,
    enabled: Boolean(session?.user),
  });

  const [isProvisioning, setIsProvisioning] = useState(false);
  const [provisioningFailed, setProvisioningFailed] = useState(false);
  const [billingRequired, setBillingRequired] = useState(false);
  // Tracks whether an automatic provision attempt has already been made this mount.
  // A ref (not state) so updating it never triggers a re-render or loops the effect.
  const hasAttemptedProvision = useRef(false);

  const provisionProjectMutation = useGraphqlMutation<
    { provisionProject: { id: string; name: string } },
    Record<string, never>
  >({
    document: provisionProjectDocument,
  });

  const revealMutation = useGraphqlMutation<
    { revealApiKey: { raw_key: string; api_key: { id: string; revealed_at: string } } },
    { id: string }
  >({
    document: revealApiKeyDocument,
  });

  const rotateMutation = useGraphqlMutation<
    {
      rotateApiKey: {
        old_api_key: { id: string; status: string };
        api_key: { id: string; name: string };
        raw_key: string;
      };
    },
    { id: string }
  >({
    document: rotateApiKeyDocument,
  });

  const revokeMutation = useGraphqlMutation<
    { revokeApiKey: { id: string; status: string; revoked_at: string | null } },
    { id: string }
  >({
    document: revokeApiKeyDocument,
  });

  const renameMutation = useGraphqlMutation<
    { renameApiKey: { id: string; name: string } },
    { id: string; name: string }
  >({
    document: renameApiKeyDocument,
  });

  useEffect(() => {
    if (!isPending && !session?.user) {
      router.push("/login");
    }
  }, [session?.user, isPending, router]);

  // Stable React Query references — guaranteed not to change between renders.
  const keysRefetch = keysQuery.refetch;
  const provisionMutateAsync = provisionProjectMutation.mutateAsync;

  // Derived data used by the auto-provision effect below — must be declared before it.
  const keys = useMemo(() => keysQuery.data?.apiKeys ?? [], [keysQuery.data?.apiKeys]);
  const projects = useMemo(() => keysQuery.data?.me?.org?.projects ?? [], [keysQuery.data]);
  const projectById = useMemo(() => new Map(projects.map((project) => [project.id, project])), [projects]);
  const filteredKeys = useMemo(() => {
    if (projectFilter === "all") {
      return keys;
    }
    return keys.filter((key) => key.project_id === projectFilter);
  }, [keys, projectFilter]);
  const firstProjectId = projects[0]?.id ?? null;
  // Scope to the first project so a default key on a different project doesn't
  // suppress repair for the project the UI and backend both target.
  const hasDefaultKey = useMemo(
    () =>
      keys.some((k) => k.purpose === CHAT_DEFAULT_PURPOSE && k.status === "active" && k.project_id === firstProjectId),
    [keys, firstProjectId],
  );

  // Auto-provision when the query has loaded and the org is missing a project or
  // a project exists but lacks an active default key (silent repair in the latter case).
  // This covers:
  //   - First-time org setup: no project has been provisioned yet.
  //   - Partial failure: project + membership exist but no active chat_default key.
  // hasAttemptedProvision gates the attempt to once per mount — a ref so updating it
  // never re-triggers this effect and avoids a retry loop on failure.
  useEffect(() => {
    const needsRepair = !firstProjectId || !hasDefaultKey;

    if (
      keysQuery.isSuccess &&
      !keysQuery.isFetching &&
      !hasAttemptedProvision.current &&
      needsRepair &&
      canCreateProjects
    ) {
      hasAttemptedProvision.current = true;

      // Only show the spinner when there is no project yet. If a project exists but
      // the default key is absent, repair silently without disabling the create button.
      const showSpinner = !firstProjectId;

      if (showSpinner) {
        setIsProvisioning(true);
      }

      provisionMutateAsync({})
        .then(() => keysRefetch())
        .catch((error) => {
          if (isBillingRequiredError(error)) {
            // Legacy path: billing was previously required to provision a project.
            // The billing middleware no longer blocks provisionProject, but this
            // handler is kept as a defensive fallback in case the error is returned
            // from a resolver-level check.
            setBillingRequired(true);
          } else if (showSpinner) {
            // Full provisioning failed for a non-billing reason.
            setProvisioningFailed(true);
          } else {
            // Silent repair failed — project still exists so the user is not blocked,
            // but surface a warning toast so the failure isn't completely invisible.
            toast.warning("Workspace repair failed. Refresh the page to retry.");
          }
        })
        .finally(() => {
          if (showSpinner) {
            setIsProvisioning(false);
          }
        });
    }
  }, [
    keysQuery.isSuccess,
    keysQuery.isFetching,
    firstProjectId,
    hasDefaultKey,
    keysRefetch,
    provisionMutateAsync,
    canCreateProjects,
  ]);

  const selectedKey = useMemo(() => keys.find((k) => k.id === selectedKeyId) ?? null, [keys, selectedKeyId]);
  const renameTrimmed = renameDraft.trim();
  const renameUnchanged = selectedKey !== null && renameTrimmed === selectedKey.name;
  const renameTaken =
    selectedKey !== null &&
    renameTrimmed !== "" &&
    keys.some(
      (key) =>
        key.id !== selectedKey.id &&
        key.project_id === selectedKey.project_id &&
        key.name.trim().toLowerCase() === renameTrimmed.toLowerCase(),
    );
  const renameTakenMessage = renameTaken
    ? `An API key named "${renameTrimmed}" already exists in this project.`
    : renameError;

  // If the user completed billing on another tab or the backend webhook provisioned the
  // project asynchronously, clear the billingRequired flag once the query shows the
  // org is fully set up. This also handles the case where Next.js keeps this
  // component mounted across a client-side round-trip to /manage/billing and back.
  useEffect(() => {
    if (billingRequired && firstProjectId && hasDefaultKey) {
      setBillingRequired(false);
    }
  }, [billingRequired, firstProjectId, hasDefaultKey]);

  // Show a persistent billing prompt when the auto-provision attempt was blocked
  // by a resolver-level billing check (legacy fallback path).
  useEffect(() => {
    if (!billingRequired) return;
    const id = toast.warning("Add a payment method to create API keys.", {
      duration: Infinity,
      action: {
        label: "Go to billing",
        onClick: () => router.push("/manage/billing"),
      },
    });
    return () => {
      toast.dismiss(id);
    };
  }, [billingRequired, router]);

  /**
   * Reveals the raw key for the first time, or toggles its visibility if already
   * fetched. The raw key is held only in React state — navigating away clears it
   * automatically, returning the display to masked without any backend call.
   */
  const handleRevealOrToggle = async (keyId: string) => {
    if (!canMutate) return;

    if (revealedKeys.has(keyId)) {
      setVisibleKeys((prev) => {
        const next = new Set(prev);
        if (next.has(keyId)) {
          next.delete(keyId);
        } else {
          next.add(keyId);
        }
        return next;
      });
      return;
    }

    setRevealingKeyId(keyId);
    try {
      const res = await revealMutation.mutateAsync({ id: keyId });
      const rawKey = res.revealApiKey.raw_key;
      setRevealedKeys((prev) => new Map(prev).set(keyId, rawKey));
      setVisibleKeys((prev) => new Set(prev).add(keyId));
      // Refresh the cache so revealed_at is up-to-date for the "already revealed" guard.
      await keysRefetch();
    } catch (e) {
      toastUserError(e, "Could not reveal API key");
    } finally {
      setRevealingKeyId(null);
    }
  };

  /**
   * Copies a raw key to the clipboard. Tries the modern Clipboard API first,
   * then falls back to the legacy execCommand approach (which does not require
   * an active user-gesture window).
   */
  const handleCopyRawKey = async (rawKey: string) => {
    try {
      await navigator.clipboard.writeText(rawKey);
      toast.success("API key copied");
    } catch {
      if (writeViaExecCommand(rawKey)) {
        toast.success("API key copied");
      } else {
        toast.error("Failed to copy — check clipboard permissions");
      }
    }
  };

  const closeConfirm = () => {
    setConfirmKind(null);
    setSelectedKeyId(null);
  };

  const handleRotateKey = async () => {
    if (!selectedKey) return;
    try {
      const res = await rotateMutation.mutateAsync({ id: selectedKey.id });
      closeConfirm();
      setCreatedApiKey({ name: res.rotateApiKey.api_key.name, rawKey: res.rotateApiKey.raw_key });
      setIntegrationCodeOpen(true);
      await keysRefetch();
    } catch (e) {
      toastUserError(e, "Could not rotate API key");
    }
  };

  const handleRevokeKey = async () => {
    if (!selectedKey) return;
    try {
      await revokeMutation.mutateAsync({ id: selectedKey.id });
      toast.success("API key revoked");
      closeConfirm();
      await keysRefetch();
    } catch (e) {
      toastUserError(e, "Could not revoke API key");
    }
  };

  const closeRename = () => {
    setRenameOpen(false);
    setRenameDraft("");
    setRenameError(null);
    setSelectedKeyId(null);
  };

  const handleRenameKey = async () => {
    if (!selectedKey || !renameTrimmed || renameUnchanged || renameTaken) {
      return;
    }
    try {
      await renameMutation.mutateAsync({ id: selectedKey.id, name: renameTrimmed });
      toast.success("API key renamed");
      closeRename();
      await keysRefetch();
    } catch (e) {
      const validation = graphqlValidationMessage(e);
      setRenameError(validation ?? "Could not rename API key");
    }
  };

  const actionsForKey = (key: DeployKeysQuery["apiKeys"][number]): RowActionItem[] => {
    if (!key.is_editable || !canMutateKeys) {
      return [];
    }
    return [
      {
        label: "Rename key",
        icon: <IconPencil className="size-4" />,
        onSelect: () => {
          setSelectedKeyId(key.id);
          setRenameDraft(key.name);
          setRenameError(null);
          setRenameOpen(true);
        },
      },
      {
        label: "Rotate key",
        icon: <IconRefresh className="size-4" />,
        disabled: !canMutate,
        disabledTooltip: canMutate ? undefined : capacityTooltip,
        onSelect: () => {
          setSelectedKeyId(key.id);
          setConfirmKind("rotate");
        },
      },
      {
        label: "Revoke key",
        icon: <IconTrash className="size-4" />,
        variant: "destructive",
        onSelect: () => {
          setSelectedKeyId(key.id);
          setConfirmKind("revoke");
        },
      },
    ];
  };

  if (isPending) {
    return <div className="flex min-h-screen items-center justify-center">Loading...</div>;
  }

  if (!session?.user) {
    return null;
  }

  // When billing is required the button is always enabled so the user can navigate to billing.
  const createDisabled = !billingRequired && (!canMutate || isProvisioning || !firstProjectId);
  const createTooltipText = billingRequired
    ? ""
    : isProvisioning
      ? "Setting up your workspace…"
      : !canMutate
        ? capacityTooltip
        : !firstProjectId
          ? !canCreateProjects
            ? ASK_ADMIN_TO_CREATE_PROJECT_HINT
            : provisioningFailed
              ? "Workspace setup failed. Please refresh the page to try again."
              : "Setting up your workspace…"
          : "";

  const createButton = (
    <Button
      className="w-full sm:w-auto"
      onClick={() => (billingRequired ? router.push("/manage/billing") : setCreateDialogOpen(true))}
      disabled={createDisabled}
    >
      <IconPlus />
      Create API key
    </Button>
  );

  return (
    <>
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-4 md:gap-6">
          <PageHeader
            title="API keys"
            description="Manage API keys for programmatic access to your resources."
            actions={
              <>
                {canMutateKeys ? <IntegrationCodeSheet /> : null}
                {canMutateKeys ? (
                  createDisabled ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex">{createButton}</span>
                      </TooltipTrigger>
                      <TooltipContent sideOffset={6} className="max-w-xs text-balance">
                        {createTooltipText}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    createButton
                  )
                ) : null}
              </>
            }
          />

          <div>
            <CapacityNoticeBanner variant="inline" />
            <div className="mt-4 flex flex-col gap-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-lg font-semibold">Active API keys</h2>
                {projects.length > 0 ? (
                  <Select value={projectFilter} onValueChange={(value) => setProjectFilter(String(value))}>
                    <SelectTrigger variant="filter" className="w-full sm:w-auto sm:min-w-40">
                      <SelectValue placeholder="View by Project">
                        <span className="inline-flex items-center gap-1">
                          <span className="text-gray-500">View by</span>
                          <span>
                            {projectFilter === "all" ? "Project" : (projectById.get(projectFilter)?.name ?? "Project")}
                          </span>
                        </span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All projects</SelectItem>
                      {projects.map((project) => (
                        <SelectItem
                          key={project.id}
                          value={project.id}
                          endAdornment={<OrgProjectColorDot color={project.color} />}
                        >
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : null}
              </div>

              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>API key</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Last used</TableHead>
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {keysQuery.isPending || isProvisioning ? (
                      <TableRow>
                        <TableCell>
                          <Skeleton className="h-5 w-32" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-5 w-28" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-7 w-44" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-5 w-24" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-5 w-20" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="ml-auto h-8 w-8" />
                        </TableCell>
                      </TableRow>
                    ) : filteredKeys.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-muted-foreground h-24 text-center">
                          {keys.length === 0 ? "No API keys yet." : "No API keys for this project."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredKeys.map((key) => {
                        const isRevealed = revealedKeys.has(key.id);
                        const isVisible = visibleKeys.has(key.id);
                        const isRevealingThis = revealingKeyId === key.id;
                        const isDefaultKey = key.purpose === CHAT_DEFAULT_PURPOSE;
                        const project = key.project_id ? projectById.get(key.project_id) : undefined;
                        const displayValue =
                          isRevealed && isVisible
                            ? (revealedKeys.get(key.id) ?? maskFromPrefix(key.key_prefix))
                            : maskFromPrefix(key.key_prefix);

                        // A key with revealed_at set was already revealed in a prior
                        // session. The server only issues the raw value once, so the
                        // eye button must be disabled unless the raw key is cached
                        // locally from this session.
                        const alreadyRevealedElsewhere = key.revealed_at !== null && !isRevealed;

                        const eyeButton =
                          !isDefaultKey || !canMutateKeys ? null : !canMutate ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    disabled
                                    type="button"
                                    aria-label="Show API key"
                                  >
                                    <IconEye className="size-3.5" />
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs text-balance">{capacityTooltip}</TooltipContent>
                            </Tooltip>
                          ) : alreadyRevealedElsewhere ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    disabled
                                    type="button"
                                    aria-label="Key already revealed"
                                  >
                                    <IconEye className="size-3.5 opacity-40" />
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs text-balance">
                                This key was already revealed. Rotate it to get a new key you can copy.
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              type="button"
                              disabled={isRevealingThis}
                              onClick={() => void handleRevealOrToggle(key.id)}
                              aria-label={isVisible ? "Hide API key" : "Show API key"}
                              data-testid={`toggle-reveal-${key.purpose}`}
                            >
                              {isVisible ? <IconEyeOff className="size-3.5" /> : <IconEye className="size-3.5" />}
                            </Button>
                          );

                        return (
                          <TableRow key={key.id}>
                            <TableCell>
                              {/* TODO: Remove the backend purpose field once it is removed from the API. */}
                              <div className="font-medium">{isDefaultKey ? "Sandbox (default)" : key.name}</div>
                            </TableCell>
                            <TableCell>
                              {project ? (
                                <OrgProjectChip name={project.name} color={project.color} />
                              ) : (
                                <span className="text-muted-foreground text-sm">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <code
                                  className={`rounded bg-muted px-2 py-1 font-mono text-sm ${
                                    isVisible ? "" : "min-w-48 sm:min-w-64"
                                  }`}
                                  data-testid={`key-display-${key.purpose}`}
                                >
                                  {displayValue}
                                </code>
                                {eyeButton}
                                {isDefaultKey && isRevealed && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    type="button"
                                    onClick={() => {
                                      const raw = revealedKeys.get(key.id);
                                      if (raw) void handleCopyRawKey(raw);
                                    }}
                                    aria-label="Copy API key"
                                    data-testid={`copy-key-${key.purpose}`}
                                  >
                                    <IconCopy className="size-3.5" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>{new Date(key.created_at).toLocaleDateString()}</TableCell>
                            <TableCell>
                              {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : "Never"}
                            </TableCell>
                            <TableCell className="text-right">
                              <RowActionsMenu label={`Actions for ${key.name}`} items={actionsForKey(key)} />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CreateApiKeyDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        projectId={projectFilter === "all" ? null : projectFilter}
        billingRequired={billingRequired}
        onBillingRequired={() => router.push("/manage/billing")}
        onCreated={(apiKey) => {
          void keysQuery.refetch();
          setCreatedApiKey(apiKey);
          setIntegrationCodeOpen(true);
        }}
      />
      <IntegrationCodeSheet
        showTrigger={false}
        open={integrationCodeOpen}
        onOpenChange={(open) => {
          setIntegrationCodeOpen(open);
          if (!open) setCreatedApiKey(null);
        }}
        newApiKey={createdApiKey}
      />

      <Dialog
        open={renameOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeRename();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename {selectedKey?.name ?? "API key"}</DialogTitle>
            <DialogDescription>This name is only shown in the dashboard. The secret does not change.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="rename-api-key-name">API key name</Label>
            <Input
              id="rename-api-key-name"
              value={renameDraft}
              onChange={(event) => {
                setRenameDraft(event.target.value);
                setRenameError(null);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void handleRenameKey();
                }
              }}
            />
            {renameTakenMessage ? <p className="text-destructive text-sm">{renameTakenMessage}</p> : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeRename}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={
                renameMutation.isPending || !renameTrimmed || renameUnchanged || renameTaken || selectedKey === null
              }
              onClick={() => void handleRenameKey()}
            >
              Rename key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmKind !== null}
        onOpenChange={(open) => {
          if (!open) closeConfirm();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmKind === "rotate"
                ? `Rotate ${selectedKey?.name ?? "API key"}?`
                : `Revoke ${selectedKey?.name ?? "API key"}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmKind === "rotate"
                ? "The current secret will stop working immediately. A new key is shown once — save it now."
                : "This secret will stop working immediately. You cannot undo this action."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={confirmKind === "revoke" ? "bg-destructive text-white hover:bg-destructive/90" : undefined}
              disabled={rotateMutation.isPending || revokeMutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (confirmKind === "rotate") {
                  void handleRotateKey();
                  return;
                }
                if (confirmKind === "revoke") {
                  void handleRevokeKey();
                }
              }}
            >
              {confirmKind === "rotate" ? "Rotate key" : "Revoke key"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default APIKeysPage;
